/**
 * BondCard - Displays a single bond in the sidebar
 *
 * Shows:
 * - Bond name (tax year - the primary identifier)
 * - Calculated principal (from candidate results, not target)
 * - Duration in years
 */

import { Box, HStack, IconButton, Text, VStack } from '@chakra-ui/react';
import { Trash2 } from 'lucide-react';
import type React from 'react';

import type { BondSeries } from '~/types/inputs';

interface BondCardProps {
  bond: BondSeries;
  /** Calculated principal from candidate results (if available) */
  calculatedPrincipal?: number;
  isSelected: boolean;
  onSelect: () => void;
  onRemove?: () => void;
}

/**
 * Calculate bond term in years from payment dates
 */
const getBondTerm = (bond: BondSeries): number => {
  const startDate = new Date(bond.firstPrincipalPaymentDate);
  const endDate = new Date(bond.finalPrincipalPaymentDate);
  const years = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return Math.round(years + 1);
};

/**
 * Format currency for compact display
 */
const formatCurrency = (amount: number): string => {
  if (amount >= 1_000_000) {
    return `$${(amount / 1_000_000).toFixed(1)}M`;
  }
  if (amount >= 1_000) {
    return `$${(amount / 1_000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
};

export const BondCard: React.FC<BondCardProps> = ({
  bond,
  calculatedPrincipal,
  isSelected,
  onSelect,
  onRemove,
}) => {
  const term = getBondTerm(bond);

  // Display calculated principal if available, otherwise show "Pending..."
  const principalDisplay = calculatedPrincipal ? formatCurrency(calculatedPrincipal) : 'Pending...';

  return (
    <Box
      p={3}
      borderRadius="md"
      bg={isSelected ? 'blue.50' : 'transparent'}
      borderWidth="1px"
      borderColor={isSelected ? 'blue.200' : 'gray.200'}
      cursor="pointer"
      _hover={{
        bg: isSelected ? 'blue.50' : 'gray.50',
        borderColor: isSelected ? 'blue.300' : 'gray.300',
      }}
      onClick={onSelect}
      transition="all 0.2s"
    >
      <VStack align="stretch" gap={1}>
        {/* Tax Year - Primary identifier */}
        <HStack justify="space-between" align="center">
          <Text fontSize="md" fontWeight="semibold" color="gray.800">
            {bond.name}
          </Text>
          {onRemove && (
            <IconButton
              aria-label={`Remove ${bond.name}`}
              size="xs"
              variant="ghost"
              colorPalette="red"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
            >
              <Trash2 size={14} />
            </IconButton>
          )}
        </HStack>

        {/* Principal - Actual calculated value */}
        <Text fontSize="sm" color="gray.700" fontWeight="medium">
          {principalDisplay}
        </Text>

        {/* Duration */}
        <Text fontSize="sm" color="gray.600">
          {term} years
        </Text>
      </VStack>
    </Box>
  );
};
